import React, { useCallback, useMemo, useRef } from "react";
import FilterAssistant from "../filterAssistant/FilterAssistant";
import { executeTableCommand } from "../filterAssistant/capabilities/tableView/tableExecutor";
import { executeNavigation } from "../filterAssistant/capabilities/dashboardNavigation/navExecutor";
import { buildNavigationTargets } from "../filterAssistant/capabilities/dashboardNavigation/navCatalog";
import { emitClientSpans, makeSpan } from "../filterAssistant/core/telemetry";
import {
    buildOttoAssetFilterFields,
    buildAssetFilterParams,
    buildAssetActiveFilters,
    toAssetSubmitShape
} from "./ottoAssetFilters";
import { createAssetCandidateSource } from "./ottoAssetOptions";

// The legacy standalone /assets route family (App.js: /assets, /assets/:section, /assets/:section/:id,
// /assets/:section/:id/:tab) — nothing in the app links to it (SideMenu points "Assets" at
// /assetmanagement/assetinfo/<clientId>/assets?parentSection=asset instead), but the container still
// renders directly on it. `section` alone cannot detect this surface: it is empty on the bare "/assets"
// AND on some embedded uses, so the check has to be the ROUTE PATTERN (`match.path`), not the params.
// Exported so renderOttoAssistant's gate is unit-testable without mounting the ~3200-line container.
export const isLegacyStandaloneAssetsRoute = matchPath =>
    matchPath === "/assets" || (typeof matchPath === "string" && matchPath.startsWith("/assets/"));

// Mounts Otto on the Asset Management grid. Two capabilities beyond navigation:
//
//  - table_view: sort / show-hide columns / pin / pagination / resets / export, through the page's
//    `tableController`. The assets grid uses the SAME shared Table + column-config machinery as
//    Recommendations, so that wiring is a 1:1 mirror (FTO-71).
//
//  - filters (LOCAL): the page's OWN filter panel, not the dashboard's. Three things differ from every
//    other Otto filter mount, all handled in ottoAssetFilters.js / ottoAssetOptions.js:
//      * the field catalog is a static array with its own vocabulary (string getListKey, isWildCardFilter);
//      * option values are NAMES under paramKey, and wildcards live under a separate `filters` object;
//      * candidate options are NOT in the filter-ai catalog view (which carries recommendations
//        vocabulary), so a page-supplied `candidateSource` routes those lookups to /assets/get_list —
//        the panel's own endpoint, with the panel's own cascade.
//
// Row filtering is therefore a real `filters` capability here, NOT table_view's column_filter — same split
// as Recommendations, which is why the tableController omits the column_filter deps.
export default function AssetsOtto({
    section,
    clientId,
    tableController,
    assetViewFieldConfig,
    getCurrentFilters,
    prepareDropdownApiParams,
    onSetLocalFilters
}) {
    const undoRef = useRef(undefined);

    // WHICH assets grid is this? The same container is embedded in the Region/Site/Building info pages and
    // in Image Management, where it shows assets already scoped to that record. Only the asset hub's own tab
    // is the Assets Table View; the rest are `scopedAssets` — same capabilities, no asset-hub destinations,
    // and no claim on the bare noun "assets". Getting this wrong would offer asset-hub navigation from a
    // project-hub page.
    const isAssetHub = section === "assetinfo";
    const page = isAssetHub ? "assets" : "scopedAssets";

    // The permission-scoped reachable pages powering `navigate` AND the page manifest. Every mount must pass
    // these or grounding runs against an EMPTY catalog and every "take me to…" fails. The clientId is what
    // unlocks the asset hub's own tabs — and it is deliberately undefined off the hub, so an embedded scoped
    // list never offers to send the user into a different client's asset hierarchy.
    const navTargets = useMemo(() => buildNavigationTargets({ clientId: isAssetHub ? clientId : undefined }), [isAssetHub, clientId]);

    // The asset catalog in Otto's field shape. Rebuilt when the per-user field config changes, because that
    // config is what marks a field hidden — and a hidden field must disappear from everything Otto offers
    // (FTO-70), not merely fail on apply.
    const filterFields = useMemo(() => buildOttoAssetFilterFields({ assetViewFieldConfig }), [assetViewFieldConfig]);

    // Stable identity: Otto's send callback depends on it. The getters inside read LIVE page state.
    const candidateSource = useMemo(
        () => createAssetCandidateSource({ getCurrentFilters, prepareDropdownApiParams }),
        [getCurrentFilters, prepareDropdownApiParams]
    );

    const onTableApply = useCallback(changes => executeTableCommand(changes, tableController), [tableController]);

    const currentFilters = typeof getCurrentFilters === "function" ? getCurrentFilters() : {};

    const onApply = useCallback(
        (payload, trace) => {
            const current = typeof getCurrentFilters === "function" ? getCurrentFilters() : {};
            const { params, clearedAll, diagnostics } = buildAssetFilterParams({ payload, filterFields, current });
            // Trace the apply — the last mile where a dropped paramKey silently no-ops (Otto says "Applied"
            // and the grid never changes). Mirrors the dashboard/Recommendations apply span, status "error"
            // on any drop. Best-effort: emitClientSpans never throws.
            if (trace && trace.requestId) {
                const dropped = (diagnostics && diagnostics.dropped) || [];
                emitClientSpans(trace.requestId, trace.sessionId, [
                    makeSpan("apply", {
                        input: { page },
                        output: { ...(diagnostics || {}), clearedAll, finalParamKeys: Object.keys(params) },
                        status: dropped.length ? "error" : "ok",
                        metadata: { dropped: dropped.length, applied: ((diagnostics && diagnostics.applied) || []).length }
                    })
                ]);
            }
            // Snapshot in the SUBMIT shape: undo hands this straight back to submitMasterFilter, so storing
            // getCurrentFilters' read shape made Undo re-commit the very nesting the apply path just fixed.
            undoRef.current = toAssetSubmitShape(current); // snapshot for Undo
            if (onSetLocalFilters) onSetLocalFilters(params);
        },
        [filterFields, getCurrentFilters, onSetLocalFilters, page]
    );

    const onUndo = useCallback(() => {
        if (undoRef.current === undefined) return;
        const snap = undoRef.current;
        undoRef.current = undefined;
        if (onSetLocalFilters) onSetLocalFilters(snap || {});
    }, [onSetLocalFilters]);

    // The panel's current selections in Otto's activeFilters shape, so multi-turn refinement, "remove the …
    // filter" and "which filters are active?" all read the real state.
    const activeFilters = useMemo(() => buildAssetActiveFilters(currentFilters, filterFields), [currentFilters, filterFields]);

    return (
        <FilterAssistant
            page={page}
            filterFields={filterFields}
            activeFilters={activeFilters}
            candidateSource={candidateSource}
            navTargets={navTargets}
            tableController={tableController}
            onTableApply={onTableApply}
            onNavigate={executeNavigation}
            onApply={onSetLocalFilters ? onApply : undefined}
            onUndo={onUndo}
        />
    );
}
